import React, { useEffect, useState } from "react";
import { ConnectedProps } from "react-redux";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Space,
  Spin,
  Typography,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import SelectExportFormat from "./SelectExportFormat";
import connector from "./Container";

const { TextArea } = Input;

const Dashboard = ({ state, actions }: ConnectedProps<typeof connector>) => {
  const { loggedUser, messages, isUploading, participants, eventInfo } = state;
  const [form] = Form.useForm();

  const [loaded, setLoaded] = useState(false);
  const [isDisabledButton, setIsDisabledButton] = useState(true);

  useEffect(() => {
    actions.getMessages();
    actions.getParticipants();
    actions.getEventInfo();
  }, []);

  useEffect(() => {
    if (messages !== null && eventInfo !== null) {
      setLoaded(true);
      return;
    }
    setLoaded(false);
  }, [messages, eventInfo]);

  const onFinish = (values: any) => {
    const msg = {
      registeredUserBos: values.registeredUserBos,
      confirmedUserBos: values.confirmedUserBos,
      confirmedUserBosOrg: values.confirmedUserBosOrg,
      confirmedUserBosSponsor: values.confirmedUserBosSponsor,
      confirmedUserBosPoz: values.confirmedUserBosPoz,
      entityId: loggedUser?.entityId,
    };

    if (!messages?.id) {
      actions.setMessages(msg);
    } else {
      isMessageChanged(values) && actions.updateMessages(msg, messages.id);
    }

    eventInfo?.[0]?.isSoldOut !== values.isSoldOut &&
      actions.updateIsSoldOut(loggedUser?.entityId, values.isSoldOut);

    eventInfo?.[0]?.isComingSoon !== values.isComingSoon &&
      actions.updateIsComingSoon(loggedUser?.entityId, values.isComingSoon);

    setIsDisabledButton(true);
  };

  const isMessageChanged = (values: any) => {
    return messages?.registeredUserBos !== values.registeredUserBos ||
      messages?.confirmedUserBos !== values.confirmedUserBos ||
      messages?.confirmedUserBosOrg !== values.confirmedUserBosOrg ||
      messages?.confirmedUserBosSponsor !== values.confirmedUserBosSponsor ||
      messages?.confirmedUserBosPoz !== values.confirmedUserBosPoz 
      ? true
      : false;
  };

  const selectFormat = () => {
    loggedUser?.entityId &&
      actions.exportFormat(
        loggedUser?.entityId,
        form.getFieldsValue().format.substring(1),
        form.getFieldsValue().exportPresentParticipants?.format("YYYY-MM-DD")
      );
  };

  const generateInvoice = () => {
    actions.exportInvoice(
      form.getFieldsValue().firstDayDateInvoice?.format("YYYY-MM-DD"),
      form.getFieldsValue().secondDayDateInvoice?.format("YYYY-MM-DD")
    );
  };

  const generateAcreditations = () => {
    actions.generateAcreditations(
      form.getFieldsValue().firstDayDateAcreditations?.format("YYYY-MM-DD"),
      form.getFieldsValue().secondDayDateAcreditations?.format("YYYY-MM-DD")
    );
  };

  const onValuesChange = (changedValues: object, values: any) => {
    if (
      isMessageChanged(values) ||
      eventInfo?.[0]?.isSoldOut !== values.isSoldOut ||
      eventInfo?.[0]?.isComingSoon !== values.isComingSoon
    ) {
      setIsDisabledButton(false);
    } else {
      setIsDisabledButton(true);
    }
  };

  return loaded ? (
    <Space direction="vertical" className="px-4 py-6 sm:p-8 w-full">
      <Form
        onValuesChange={onValuesChange}
        form={form}
        name="dashboard"
        onFinish={onFinish}
        layout="vertical"
        requiredMark={false}
      >
        <Row>
          <Col span={24} md={12}>
            <div className="w-[95%] md:w-[90%] lg:w-4/5 max-w-[400px]">
              <Typography className="mb-2">
                Unesi tekst poruke za registrovanog korisnika [predračun]
              </Typography>
              <Form.Item
                initialValue={messages?.registeredUserBos}
                name="registeredUserBos"
                rules={[
                  {
                    required: true,
                    message: "Obavezno polje!",
                  },
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder="Upiši tekst na bosanskom jeziku"
                  className="messagesBorder"
                />
              </Form.Item>

              <Typography className="mb-2">
                Unesi tekst poruke za odobrenog korisnika
              </Typography>
              <Form.Item
                initialValue={messages?.confirmedUserBos}
                name="confirmedUserBos"
                rules={[
                  {
                    required: true,
                    message: "Obavezno polje!",
                  },
                ]}
                className="mb-10"
              >
                <TextArea
                  rows={3}
                  placeholder="Upiši tekst na bosanskom jeziku"
                  className="messagesBorder"
                />
              </Form.Item>

              <Typography className="mb-2">
                Unesi tekst poruke za odobrenog korisnika (Organizator)
              </Typography>
              <Form.Item
                initialValue={messages?.confirmedUserBosOrg}
                name="confirmedUserBosOrg"
                rules={[
                  {
                    required: true,
                    message: "Obavezno polje!",
                  },
                ]}
                className="mb-10"
              >
                <TextArea
                  rows={3}
                  placeholder="Upiši tekst na bosanskom jeziku"
                  className="messagesBorder"
                />
              </Form.Item>

              <Typography className="mb-2">
                Unesi tekst poruke za odobrenog korisnika (Sponzor)
              </Typography>
              <Form.Item
                initialValue={messages?.confirmedUserBosSponsor}
                name="confirmedUserBosSponsor"
                rules={[
                  {
                    required: true,
                    message: "Obavezno polje!",
                  },
                ]}
                className="mb-10"
              >
                <TextArea
                  rows={3}
                  placeholder="Upiši tekst na bosanskom jeziku"
                  className="messagesBorder"
                />
              </Form.Item>

              <Typography className="mb-2">
                Unesi tekst poruke za odobrenog korisnika (Pozivnica)
              </Typography>
              <Form.Item
                initialValue={messages?.confirmedUserBosPoz}
                name="confirmedUserBosPoz"
                rules={[
                  {
                    required: true,
                    message: "Obavezno polje!",
                  },
                ]}
                className="mb-10"
              >
                <TextArea
                  rows={3}
                  placeholder="Upiši tekst na bosanskom jeziku"
                  className="messagesBorder"
                />
              </Form.Item>

              <Typography className="mb-3 font-bold">
                Odaberi datume za izvoz predračuna
              </Typography>
              <div className="flex gap-8">
                <div>
                  <Form.Item name="firstDayDateInvoice">
                    <DatePicker
                      className="rounded-sm xl:w-[144px]"
                      placeholder="Odaberi datum"
                      onChange={() =>
                        form.setFieldsValue({ secondDayDateInvoice: undefined })
                      }
                      allowClear={false}
                    />
                  </Form.Item>
                </div>

                <div>
                  <Form.Item name="secondDayDateInvoice">
                    <DatePicker
                      className="rounded-sm xl:w-[144px]"
                      placeholder="Odaberi datum"
                      disabledDate={(current) =>
                        !current ||
                        (form.getFieldsValue().firstDayDateInvoice &&
                          current.isBefore(
                            form
                              .getFieldsValue()
                              .firstDayDateInvoice.add(1, "day"),
                            "day"
                          ))
                      }
                      allowClear={false}
                    />
                  </Form.Item>
                </div>
              </div>

              <Button
                className="bg-custom-pink-900 text-white flex items-center justify-center font-medium text-base w-full xl:w-auto h-10 rounded-sm border-custom-pink-900 shadow"
                onClick={generateInvoice}
                disabled={participants.length === 0}
              >
                IZVEZI PREDRAČUNE
              </Button>

              <Typography className="mt-9 mb-3 font-bold">
                Odaberi datume za izvoz akreditacija
              </Typography>
              <div className="flex gap-8">
                <div>
                  <Form.Item name="firstDayDateAcreditations">
                    <DatePicker
                      className="rounded-sm xl:w-[144px]"
                      placeholder="Odaberi datum"
                      onChange={() =>
                        form.setFieldsValue({
                          secondDayDateAcreditations: undefined,
                        })
                      }
                      allowClear={false}
                    />
                  </Form.Item>
                </div>

                <div>
                  <Form.Item name="secondDayDateAcreditations">
                    <DatePicker
                      className="rounded-sm xl:w-[144px]"
                      placeholder="Odaberi datum"
                      disabledDate={(current) =>
                        !current ||
                        (form.getFieldsValue().firstDayDateAcreditations &&
                          current.isBefore(
                            form
                              .getFieldsValue()
                              .firstDayDateAcreditations.add(1, "day"),
                            "day"
                          ))
                      }
                      allowClear={false}
                    />
                  </Form.Item>
                </div>
              </div>

              <Button
                className="bg-custom-pink-900 text-white flex items-center justify-center font-medium text-base w-full xl:w-auto h-10 rounded-sm border-custom-pink-900 shadow"
                onClick={generateAcreditations}
                disabled={
                  participants.filter(
                    (participant: any) => participant.accreditationDate
                  ).length === 0
                }
              >
                IZVEZI AKREDITACIJE
              </Button>
            </div>
          </Col>
          <Col span={24} md={12} className="md:flex md:justify-end">
            <div className="xl:w-[85%]">
              <SelectExportFormat
                selectFormat={selectFormat}
                participants={participants}
              />
            </div>
          </Col>
        </Row>

        <Form.Item
          initialValue={eventInfo?.[0]?.isSoldOut}
          name="isSoldOut"
          className="w-fit mt-10 md:mt-16"
          valuePropName="checked"
        >
          <Checkbox className="text-custom-pink-900 font-bold">
            AKTIVIRAJ OPCIJU RASPRODATO
          </Checkbox>
        </Form.Item>

        <Form.Item
          initialValue={eventInfo?.[0]?.isComingSoon}
          name="isComingSoon"
          className="w-fit"
          valuePropName="checked"
        >
          <Checkbox className="text-custom-pink-900 font-bold">
            AKTIVIRAJ OPCIJU REGISTRACIJA USKORO
          </Checkbox>
        </Form.Item>

        <Form.Item>
          <Button
            disabled={isDisabledButton || isUploading}
            htmlType="submit"
            className="bg-custom-pink-900 text-white flex items-center justify-center font-medium text-base h-10 w-[95%] md:w-[114px] rounded-sm border-custom-pink-900 shadow mb-4 md:mb-0"
          >
            {!isUploading ? (
              "SNIMI"
            ) : (
              <LoadingOutlined style={{ fontSize: 18 }} />
            )}
          </Button>
        </Form.Item>
      </Form>
    </Space>
  ) : (
    <div className="w-full h-full flex justify-center items-center ">
      <Spin indicator={<LoadingOutlined style={{ fontSize: 46 }} spin />} />
    </div>
  );
};

export default Dashboard;
